import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { 
  getPagePath, 
  type Lang, 
  resolvePageKeyFromSlug, 
  resolveHubIdFromSlug, 
  resolveArticleIdFromSlug,
  buildUsefulHubPath,
  buildUsefulArticlePath,
  getUsefulRootPath,
  getUsefulRootSlug
} from '@/lib/localizedRoutes';
import { cn } from '@/lib/utils';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileDetailHeader } from './MobileDetailHeader';
import { trackClick } from '@/lib/analytics';

const NAV_SLUGS = [
  { slug: 'catalog', label: 'nav.catalog', id: '01' },
  { slug: 'useful', label: 'nav.useful', id: '02' },
  { slug: 'portfolio', label: 'nav.portfolio', id: '03' },
  { slug: 'news', label: 'nav.news', id: '04' },
  { slug: 'about', label: 'nav.about', id: '05' },
  { slug: 'esg', label: 'nav.esg', id: '06' },
  { slug: 'where-to-buy', label: 'nav.whereToBuy', id: '07' },
];

const VALID_LANGS = ['ru', 'en', 'uz'];

/** Returns the lang prefix from a pathname (e.g. '/ru/catalog' → 'ru'), or '' */
function getLangPrefix(pathname: string): string {
  const seg = pathname.split('/')[1];
  return VALID_LANGS.includes(seg) ? seg : '';
}

/** Returns the path without the leading lang segment (e.g. '/ru/catalog/foo' → '/catalog/foo') */
function stripLangPrefix(pathname: string): string {
  const parts = pathname.split('/');
  if (VALID_LANGS.includes(parts[1])) {
    return '/' + parts.slice(2).join('/');
  }
  return pathname;
}

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const langPrefix = getLangPrefix(location.pathname);
  const pathWithoutLang = stripLangPrefix(location.pathname);

  // Resolution helpers for localized paths
  const currentLang = (langPrefix || language) as Lang;
  const segments = location.pathname.split('/').filter(Boolean).slice(1); // skip lang
  const firstSlug = segments[0] || '';
  const pageKey = resolvePageKeyFromSlug(firstSlug, currentLang);
  
  const isUseful = pageKey === 'useful';
  const isArticlePage = isUseful && segments.length >= 3;
  const isHubPage = isUseful && segments.length === 2;
  const isProductPage = pathWithoutLang.startsWith('/catalog/') && pathWithoutLang !== '/catalog';

  // Internal pages that should use the simple detail header (back button + logo)
  const isMobileDetailView = isMobile && (isProductPage || isArticlePage || isHubPage);
  const shouldHideHamburger = isArticlePage || isProductPage || isHubPage;

  // Determine if we're on a main page (for mobile tab navigation)
  const isMainPageKey = resolvePageKeyFromSlug(pathWithoutLang.replace(/^\//, ''), language as Lang);
  const isMainPage = (pathWithoutLang === '/' || isMainPageKey !== null || pathWithoutLang === '/catalog') && pathWithoutLang.split('/').filter(Boolean).length <= 1;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenuOpen]);

  const handleBack = () => {
    const base = langPrefix ? `/${langPrefix}` : '';
    if (isArticlePage) {
      const hubSlug = segments[1];
      const hubId = resolveHubIdFromSlug(hubSlug, currentLang);
      if (hubId) {
        navigate(buildUsefulHubPath(currentLang, hubId));
      } else {
        navigate(getUsefulRootPath(currentLang));
      }
      return;
    }

    if (isHubPage) {
      navigate(getUsefulRootPath(currentLang));
      return;
    }

    if (isProductPage) {
      const parts = pathWithoutLang.split('/').filter(Boolean);
      // parts = ['catalog', 'spc-laminate', 'some-product-slug']
      const validCategories = ['spc-laminate', 'spc-panels', 'carpet-tile', 'accessories'];
      if (parts.length >= 3 && validCategories.includes(parts[1])) {
        navigate(`${base}/catalog/${parts[1]}`);
      } else {
        navigate(`${base}/catalog`);
      }
    }
  };

  const languages = [
    { code: 'uz', label: 'UZ' },
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'EN' },
  ];

  // Switch language by replacing the lang prefix and translating slugs in the current URL
  const handleLangSwitch = (newLang: string) => {
    const targetLang = newLang as Lang;
    
    // 1. Home
    if (segments.length === 0) {
      navigate(`/${targetLang}`);
      return;
    }

    // 2. Useful Section
    if (isUseful) {
      if (segments.length === 1) {
        navigate(getUsefulRootPath(targetLang));
        return;
      }
      
      const hubSlug = segments[1];
      const hubId = resolveHubIdFromSlug(hubSlug, currentLang) || hubSlug;
      
      if (segments.length === 2) {
        navigate(buildUsefulHubPath(targetLang, hubId));
        return;
      }
      
      const articleSlug = segments[2];
      const articleId = resolveArticleIdFromSlug(hubSlug, articleSlug, currentLang) || articleSlug;
      navigate(buildUsefulArticlePath(targetLang, hubId, articleId));
      return;
    }

    // 3. Standalone Pages
    if (pageKey) {
      navigate(getPagePath(pageKey, targetLang));
      return;
    }

    // 4. Default (Catalog, etc.)
    const rest = stripLangPrefix(location.pathname) || '/';
    const newPath = `/${newLang}${rest === '/' ? '' : rest}`;
    navigate(newPath + (location.search || '') + (location.hash || ''));
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-all duration-500 overscroll-none",
        "pt-[env(safe-area-inset-top)]", // Ensure header content is below safe area
        isScrolled || isMobileDetailView
          ? (isMobileDetailView ? "bg-[#13100B] border-b border-white/5 py-3 lg:py-3" : "bg-background/95 backdrop-blur-md border-b border-white/5 py-3 lg:py-3 mirror-style")
          : "bg-transparent py-4 lg:py-6",
        // Hide header on mobile/tablet main pages (use MobileTabNavigation instead)
        isMainPage && "hidden lg:block"
      )}
    >
      <div className="container-custom flex items-center justify-between gap-4">
        {isMobileDetailView ? (
          <MobileDetailHeader
            onBack={handleBack}
            onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            isMenuOpen={isMobileMenuOpen}
            showMenu={!shouldHideHamburger}
          />
        ) : (
          <>
            {/* Default Header Layout */}
            {/* Left Section: Logo & Back Button */}
            <div className="flex items-center gap-4 lg:gap-8 min-w-[140px] xl:min-w-[200px]">
              <Link to={`/${language}`} className="flex items-center gap-2 lg:gap-4 shrink-0" onClick={() => setIsMobileMenuOpen(false)}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfrUaM52DXdWd1f2WHAbNwcoVhba2%2F08__3e9c1a05.png?alt=media&token=08603184-d1c7-42c9-b08d-947d315212e8"
                  alt="SARGO"
                  className="h-6 sm:h-8 lg:h-10 w-auto object-contain"
                />
              </Link>

              {(isProductPage || isArticlePage) && (
                <motion.button
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={handleBack}
                  className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/60 hover:text-primary transition-colors border-l border-white/10 pl-4 lg:pl-8 py-1 shrink-0"
                >
                  <ArrowLeft size={14} className="text-primary" />
                  <span className="hidden sm:inline">{t('common.back')}</span>
                </motion.button>
              )}
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center justify-center flex-1 gap-1 xl:gap-2 relative whitespace-nowrap overflow-hidden">
              {NAV_SLUGS.map((item) => {
                const fullPath = getPagePath(item.slug, language as Lang);
                const localizedSlug = fullPath.split('/').pop() || '';
                
                const itemRootSlug = getUsefulRootSlug(language as Lang);
                const isActive = (item.slug === 'useful' && location.pathname.includes(`/${itemRootSlug}`)) ||
                  (item.slug === 'catalog' && pathWithoutLang.startsWith('/catalog')) ||
                  (item.slug === 'news' && (pathWithoutLang.startsWith('/novosti/') || pathWithoutLang.startsWith('/yangiliklar/') || pathWithoutLang.startsWith('/news/'))) ||
                  (pageKey === item.slug);

                return (
                  <Link
                    key={item.slug}
                    to={fullPath}
                    data-active={isActive}
                    className={cn(
                      "group relative px-2 xl:px-4 py-2 flex flex-col items-center gap-1 transition-all duration-300 shrink-0 z-10",
                      isActive ? "text-primary" : "text-white/40 hover:text-white"
                    )}
                    onClick={() => trackClick(`nav-${item.slug}`)}
                  >
                    <span className="text-[9px] font-black tracking-widest opacity-40 group-hover:opacity-100 transition-opacity">
                      {item.id}
                    </span>
                    <span className="text-[10px] xl:text-[11px] uppercase tracking-[0.2em] font-black">
                      {t(item.label)}
                    </span>
                  </Link>
                );
              })}
            </nav>

            {/* Right Section: Dealer & Lang */}
            <div className="hidden lg:flex items-center justify-end gap-3 xl:gap-6 min-w-[200px] xl:min-w-[280px]">
              <Link
                to={getPagePath('become-dealer', language as Lang)}
                className="mirror-button py-2 px-4 xl:px-6 text-[10px] font-black tracking-[0.2em] shrink-0"
                onClick={() => trackClick('become-dealer-header')}
              >
                {t('nav.becomeDealer')}
              </Link>

              <div className="flex items-center gap-1 bg-white/5 p-1 border border-white/5 shrink-0">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangSwitch(lang.code)}
                    className={cn(
                      "px-1.5 xl:px-2 py-1 text-[10px] font-black transition-all duration-300",
                      language === lang.code
                        ? "bg-primary text-white shadow-[0_0_15px_rgba(176,138,90,0.5)]"
                        : "text-white/40 hover:text-white"
                    )}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Menu Button */}
            {!shouldHideHamburger && (
              <button
                className="lg:hidden text-white p-2 hover:bg-white/5 transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </>
        )}
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 bg-background z-[60] lg:hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <Link to={`/${language}`} onClick={() => setIsMobileMenuOpen(false)}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfrUaM52DXdWd1f2WHAbNwcoVhba2%2F08__3e9c1a05.png?alt=media&token=08603184-d1c7-42c9-b08d-947d315212e8"
                  alt="SARGO"
                  className="h-6 w-auto"
                />
              </Link>
              <button
                className="text-white p-2"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8">
              <nav className="flex flex-col gap-6">
                {NAV_SLUGS.map((item, idx) => {
                  const fullPath = getPagePath(item.slug, language as Lang);
                  const localizedSlug = fullPath.split('/').pop() || '';
                  
                  const itemRootSlug = getUsefulRootSlug(language as Lang);
                  const isActive = (item.slug === 'useful' && location.pathname.includes(`/${itemRootSlug}`)) ||
                    (item.slug === 'catalog' && pathWithoutLang.startsWith('/catalog')) ||
                    (item.slug === 'news' && (pathWithoutLang.startsWith('/novosti/') || pathWithoutLang.startsWith('/yangiliklar/') || pathWithoutLang.startsWith('/news/'))) ||
                    (pageKey === item.slug);

                  return (
                    <motion.div
                      key={item.slug}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <Link
                        to={fullPath}
                        className={cn(
                          "text-2xl font-black uppercase tracking-[0.2em] transition-colors",
                          isActive ? "text-primary" : "text-white/60"
                        )}
                        onClick={() => { setIsMobileMenuOpen(false); trackClick(`nav-mobile-${item.slug}`); }}
                      >
                        <span className="text-[10px] text-primary mr-4 opacity-50">{item.id}</span>
                        {t(item.label)}
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>

              <div className="mt-auto space-y-8">
                <Link
                  to={getPagePath('become-dealer', language as Lang)}
                  className="mirror-button block text-center w-full py-5 text-sm font-black tracking-[0.2em]"
                  onClick={() => { setIsMobileMenuOpen(false); trackClick('become-dealer-mobile-menu'); }}
                >
                  {t('nav.becomeDealer')}
                </Link>

                <div className="space-y-4">
                  <span className="text-[10px] uppercase tracking-widest text-white/30 block text-center">Select Language</span>
                  <div className="flex justify-center gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          handleLangSwitch(lang.code);
                          setIsMobileMenuOpen(false);
                        }}
                        className={cn(
                          "flex-1 py-3 text-xs font-black border border-white/10 transition-all duration-300",
                          language === lang.code
                            ? "bg-primary text-white border-primary shadow-[0_0_20px_rgba(176,138,90,0.4)]"
                            : "text-white/40"
                        )}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};